import React, { createContext, useContext, useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, X } from 'lucide-react';

import { humanizeErrorText } from '../utils/apiError';

export type ToastType = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  type: ToastType;
  message: string;
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let nextId = 1;

const ICONS: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle size={16} color="#16a34a" />,
  error:   <XCircle size={16} color="#dc2626" />,
  info:    <AlertCircle size={16} color="#2563eb" />,
};

const COLORS: Record<ToastType, { bg: string; border: string; text: string }> = {
  success: { bg: '#f0fdf4', border: '#86efac', text: '#166534' },
  error:   { bg: '#fef2f2', border: '#fca5a5', text: '#991b1b' },
  info:    { bg: '#eff6ff', border: '#93c5fd', text: '#1e40af' },
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    const t = timers.current.get(id);
    if (t) { clearTimeout(t); timers.current.delete(id); }
  }, []);

  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    const id = nextId++;
    const text = type === 'error' ? humanizeErrorText(message) : message;
    setToasts(prev => [...prev, { id, type, message: text }]);
    if (type !== 'error') {
      const timer = setTimeout(() => dismiss(id), 4000);
      timers.current.set(id, timer);
    }
  }, [dismiss]);

  useEffect(() => () => { timers.current.forEach(clearTimeout); timers.current.clear(); }, []);

  const success = useCallback((msg: string) => showToast(msg, 'success'), [showToast]);
  const error   = useCallback((msg: string) => showToast(msg, 'error'),   [showToast]);
  const info    = useCallback((msg: string) => showToast(msg, 'info'),    [showToast]);

  const value = useMemo<ToastContextValue>(
    () => ({ showToast, success, error, info }),
    [showToast, success, error, info],
  );

  return (
    <ToastContext.Provider value={value}>
      <style>{`
        [data-field-error="true"] input, [data-field-error="true"] select,
        [data-field-error="true"] textarea, [data-field-error="true"] [role="combobox"],
        [aria-invalid="true"] { border-color: #dc2626 !important; box-shadow: 0 0 0 1px #dc2626 !important; }
        [data-field-error="true"] { scroll-margin-block: 100px; }
      `}</style>
      {children}
      {toasts.length > 0 && (
        <div style={{
          position: 'fixed', bottom: 20, right: 20, zIndex: 99999,
          display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end', maxHeight: '70vh', overflowY: 'auto', maxWidth: 'calc(100vw - 32px)',
        }}>
          {toasts.map(t => {
            const c = COLORS[t.type];
            return (
              <div
                key={t.id}
                role={t.type === 'error' ? 'alert' : 'status'}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: c.bg, border: `1px solid ${c.border}`, color: c.text,
                  borderRadius: 8, padding: '10px 14px',
                  fontSize: 13, fontWeight: 500, maxWidth: 360,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                  animation: 'slideInRight 0.2s ease',
                }}
              >
                {ICONS[t.type]}
                <span style={{ flex: 1 }}>{t.message}</span>
                <button
                  aria-label="Закрыть уведомление"
                  onClick={() => dismiss(t.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.text, opacity: 0.6, display: 'flex', padding: 0 }}
                >
                  <X size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}
